import { BigNumber } from 'ethers';
import { keccak256 } from 'ethers/lib/utils';
import XorShift from './xorshift';

function hash(str: string) {
  // Nabbed from https://stackoverflow.com/a/7616484
  // The outcome hash can be negative, which causes errors -- might be worth changing to a different function
  let hash = 0;
  let chr;
  if (str.length === 0) return hash;
  for (let i = 0; i < str.length; i++) {
    chr = str.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return hash;
}

export default class PRNG {
  protected _seeds: number[];
  protected _startSeedIndex: number;
  protected _prngs: { [key: string]: XorShift };

  constructor(
    seedData: string,
    mintBlockNumber: number,
    mintTimestamp: number
  ) {
    // seedData is expected to the Crucible tokenId in hex form
    // This is 160bits of data, so we need to break it into five 32bit integers of which four will be used to seed a PRNG instance
    // Ideally some clever chap can find a PRNG that takes 160bit seed but I am not that chap
    // We'll make do by seeding multiple PRNG instances with different subsets of the five seeds, it kinda works...

    if (!seedData || !mintBlockNumber || !mintTimestamp) {
      throw new Error('PRNG error, missing data');
    }

    const id = BigNumber.from(seedData).toHexString();
    const blockNumber = BigNumber.from(mintBlockNumber).toHexString().slice(2);
    const timestamp = BigNumber.from(mintTimestamp).toHexString().slice(2);
    const finalSeed = keccak256(id.concat(blockNumber).concat(timestamp));
    let trimmedSeedData = finalSeed;

    if (trimmedSeedData.startsWith('0x')) {
      trimmedSeedData = trimmedSeedData.slice(2);
    }

    this._seeds = [];
    while (this._seeds.length < 5) {
      if (trimmedSeedData.length > 0) {
        // In hex format 32 bits are 8 characters
        const seedHexString = trimmedSeedData.slice(-8);
        trimmedSeedData = trimmedSeedData.slice(0, -8);
        this._seeds.push(parseInt(seedHexString, 16));
      } else {
        // Rare tokenId with lots of leading zeroes I guess
        this._seeds.push(0);
      }
    }
    // We'll increment this for each PRNG instance we make, and use it to pick the starting seed
    this._startSeedIndex = 0;
    this._prngs = {};
  }

  randomInt(
    channelName: string,
    outcomeCount: number = Number.MAX_SAFE_INTEGER
  ) {
    if (!this._prngs[channelName]) {
      this._createPRNG(channelName);
    }
    // Probably needs to use .randomint but I can't be bothered to figure out doing it correctly right now
    const rn = this._prngs[channelName].random();
    return Math.floor(rn * outcomeCount);
  }

  randomFloat(channelName: string) {
    if (!this._prngs[channelName]) {
      this._createPRNG(channelName);
    }
    return this._prngs[channelName].random();
  }

  _createPRNG(channelName: string) {
    let startSeedIndex;
    if (channelName === 'name') {
      // For legacy purposes the name channel must use a specific startSeedIndex
      // This is so that the names generated match up with previous released crucible lens app
      startSeedIndex = 2;
    } else {
      // Hash the channelName and use that to determine which seed combo is used
      // This means order in which channels are first accessed does not impact their RNG sequences
      // Added a fix to guarantee the hash is always positive
      startSeedIndex = Math.abs(hash(channelName));
    }
    const seeds = [];
    for (let i = 0; i < 4; i++) {
      const seedIndex = (startSeedIndex + i) % this._seeds.length;
      seeds.push(this._seeds[seedIndex]);
    }
    this._prngs[channelName] = new XorShift(seeds);
  }

  deletePrngChannel(channelName: string) {
    delete this._prngs[channelName];
  }
}
